import React, { useState, useEffect } from 'react';
import { getItemJSON, parseJSONSafe, setItemJSON, removeItem, clearAllSession } from '../utils/storage';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CandidateDashboard.css';
import showPopup from '../utils/showPopup';

function CandidateDashboard({ setUser }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Utility function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      // Return in YYYY-MM-DD format
      return date.toISOString().split('T')[0];
    } catch (error) {
      return 'N/A';
    }
  };
  
  // State management
  const [candidateData, setCandidateData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [workExperience, setWorkExperience] = useState([]);
  const [resume, setResume] = useState(null);
  const [searchFilters, setSearchFilters] = useState({
    jobTitle: '',
    location: '',
    sector: '',
    jobType: '',
    experience: '',
    ageLimit: '',
    annualSalary: '',
    qualifications: ''
  });
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [previewFile, setPreviewFile] = useState(null); // { url, label } for file preview
  const [formData, setFormData] = useState({
    candidateName: '',
    dateOfBirth: '',
    gender: '',
    mobileNumber: '',
    emailId: '',
    district: '',
    city: '',
    pincode: '',
    preferredJobLocation: '',
    educationalQualification: '',
    preferredJobSector: '',
    skills: [],
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Job types and experience options
  const jobTypes = ['Full Time', 'Part Time', 'Contract', 'Internship', 'Freelance'];
  const experienceLevels = ['Fresher', '1-2 years', '3-5 years', '5-10 years', '10+ years'];
  const ageLimits = ['18-25', '26-35', '36-45', '46-55', '55+'];

  // Sample recommended jobs based on qualifications
  const sampleJobs = [
    {
      id: 1,
      title: 'Software Engineer',
      company: 'Tech Solutions Ltd',
      location: 'Mumbai, Maharashtra',
      sector: 'IT/Computer - Software',
      jobType: 'Full Time',
      experience: '3-5 years',
      salary: '₹8-12 LPA',
      qualifications: 'B.Tech in Computer Science',
      postedDate: '2 days ago',
      status: 'Active'
    },
    {
      id: 2,
      title: 'Data Analyst',
      company: 'Analytics Pro',
      location: 'Bangalore, Karnataka',
      sector: 'KPO/Research/Analytics',
      jobType: 'Full Time',
      experience: '1-2 years',
      salary: '₹6-9 LPA',
      qualifications: 'B.Tech/MCA',
      postedDate: '3 days ago',
      status: 'Active'
    },
    {
      id: 3,
      title: 'Marketing Manager',
      company: 'Global Marketing Co',
      location: 'Delhi, NCR',
      sector: 'Marketing',
      jobType: 'Full Time',
      experience: '5-10 years',
      salary: '₹12-18 LPA',
      qualifications: 'MBA in Marketing',
      postedDate: '4 days ago',
      status: 'Active'
    }
  ];

  // Function to fetch latest candidate data from backend
  const fetchCandidateData = async (candidateId) => {
    try {
      const response = await fetch(`https://tstranscareers.in/api/candidate-profile/${candidateId}`);
      if (response.ok) {
        const data = await response.json();
        setCandidateData(data);
        // Update sessionStorage with fresh data
        setItemJSON('candidateData', data);
        return data;
      } else {
        console.error('Failed to fetch candidate profile');
        return null;
      }
    } catch (error) {
      console.error('Error fetching candidate profile:', error);
      return null;
    }
  };

  // Load candidate data on component mount
  useEffect(() => {
    const storedData = getItemJSON('candidateData');
    const loginData = getItemJSON('candidateLoggedIn');

    if (storedData && loginData) {
      // Notify App component that user is logged in
      setUser(loginData);
      
      // First load stored data immediately for faster UI
      setCandidateData(storedData);
      
      // Then fetch fresh data from backend
      fetchCandidateData(storedData.id).then((freshData) => {
        if (freshData) {
          // Load work experience from sessionStorage or set default
          const storedExperience = getItemJSON(`workExperience_${freshData.id}`);
          if (storedExperience) {
            setWorkExperience(storedExperience);
          }
          
          // Load applied jobs from backend
          fetchAppliedJobs();

          // 🆕 Load saved jobs from backend
          fetchSavedJobs();

          // Load resume if previously uploaded
          try {
            const storedResume = getItemJSON(`candidateResume_${freshData.id}`);
            if (storedResume) {
              setResume(storedResume);
            }
          } catch (err) {
            console.warn('Error reading stored resume', err);
          }
          
          // Fetch all active jobs as recommended jobs
          fetch('https://tstranscareers.in/api/api/jobs')
            .then(res => res.json())
            .then(data => {
              if (data.success) {
                setRecommendedJobs(data.jobs);
                console.log('✅ Loaded recommended jobs:', data.jobs.length);
              }
            })
            .catch(err => console.error('Error fetching jobs:', err));
        }
      });
    } else {
      navigate('/login');
    }
  }, [navigate, setUser]);

  // Track viewport width to decide whether to show mobile-only nav
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch applied jobs when user switches to the applied tab
  useEffect(() => {
    if (activeTab === 'applied' && candidateData?.id) {
      fetchAppliedJobs();
    }
  }, [activeTab, candidateData?.id]);

  // Fetch saved jobs when user switches to the saved tab
  useEffect(() => {
    if (activeTab === 'saved' && candidateData?.id) {
      fetchSavedJobs();
    }
  }, [activeTab, candidateData?.id]);

  // Handle profile editing
  const handleProfileEdit = () => {
    setIsEditing(true);
  };

  const handleProfileSave = async () => {
    try {
      // Send updated profile to backend
      // Ensure dateOfBirth is ISO yyyy-mm-dd before sending. If in dd-mm-yyyy, convert.
      const payload = { ...candidateData };
      if (payload.dateOfBirth && typeof payload.dateOfBirth === 'string') {
        // dd-mm-yyyy -> yyyy-mm-dd
        const dmy = payload.dateOfBirth.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dmy) {
          payload.dateOfBirth = `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
        } else {
          // if it's like yyyy-mm-dd already or other parseable, leave as-is
        }
      }

      const res = await fetch(`https://tstranscareers.in/api/candidate-profile/${candidateData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setItemJSON('candidateData', candidateData);
        setIsEditing(false);
        await showPopup('Profile updated successfully!', 'Success', { type: 'success' });
      } else {
        await showPopup('Failed to update profile. Please try again.', 'Error', { type: 'error' });
      }
    } catch (error) {
      await showPopup('Error updating profile.', 'Error', { type: 'error' });
    }
  };

  const handleProfileCancel = () => {
    // Reload original data
    const storedData = getItemJSON('candidateData');
    if (storedData) {
      setCandidateData(storedData);
    }
    setIsEditing(false);
  };

  // Handle input changes for profile editing
  const handleProfileChange = (field, value) => {
    setCandidateData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle resume upload - Upload to backend and replace existing
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      await showPopup('Resume file size should be less than 5MB', 'Validation', { type: 'info' });
      return;
    }

    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      await showPopup('Please upload only PDF, DOC, or DOCX files', 'Validation', { type: 'info' });
      return;
    }

    try {
      // Create FormData to send file to backend
      const formData = new FormData();
      formData.append('resumeFile', file);

      console.log('📤 Uploading new resume to backend...');
      console.log('📎 File name:', file.name);
      console.log('📎 File size:', file.size);
      console.log('  File type:', file.type);
      console.log('📎 Candidate ID:', candidateData.id);

      // Upload to backend
      const response = await fetch(`https://tstranscareers.in/api/candidate-profile/${candidateData.id}/resume`, {
        method: 'PUT',
        body: formData
      });

      console.log('📡 Response status:', response.status);

      const data = await response.json();
      console.log('📡 Response data:', data);

      if (response.ok && data.success) {
        console.log('✅ Resume uploaded successfully');
        
        // Update local state with new resume filename
        const updatedCandidateData = {
          ...candidateData,
          resumeFile: data.resumeFile
        };
        setCandidateData(updatedCandidateData);
        setItemJSON('candidateData', updatedCandidateData);

        await showPopup(data.message || 'Resume uploaded successfully!', 'Success', { type: 'success' });
        
        // Refresh candidate data from backend
        await fetchCandidateData(candidateData.id);
      } else {
        console.error('❌ Upload failed:', data);
        await showPopup(data.message || 'Failed to upload resume. Please try again.', 'Error', { type: 'error' });
      }
    } catch (error) {
      console.error('❌ Error uploading resume:', error);
      await showPopup(`Error uploading resume: ${error.message || 'Please try again.'}`, 'Error', { type: 'error' });
    }

    // Clear the file input
    e.target.value = '';
  };

  // Handle work experience
  const addWorkExperience = () => {
    const newExperience = {
      id: Date.now(),
      company: '',
      designation: '',
      annualSalary: '',
      joiningDate: '',
      leavingDate: '',
      reason: '',
      isEditing: true
    };
    setWorkExperience(prev => [...prev, newExperience]);
  };

  const saveWorkExperience = (id) => {
    setWorkExperience(prev => prev.map(exp => 
      exp.id === id ? { ...exp, isEditing: false } : exp
    ));
    
    // Save to sessionStorage
    setItemJSON(`workExperience_${candidateData.id}`, workExperience);
  };

  const editWorkExperience = (id) => {
    setWorkExperience(prev => prev.map(exp => 
      exp.id === id ? { ...exp, isEditing: true } : exp
    ));
  };

  const deleteWorkExperience = (id) => {
    (async () => {
      const ok = await showPopup('Are you sure you want to delete this work experience?', 'Confirm', { type: 'info', confirm: true });
      if (!ok) return;
      const updatedExperience = workExperience.filter(exp => exp.id !== id);
      setWorkExperience(updatedExperience);
      setItemJSON(`workExperience_${candidateData.id}`, updatedExperience);
    })();
  };

  const handleWorkExperienceChange = (id, field, value) => {
    setWorkExperience(prev => prev.map(exp => 
      exp.id === id ? { ...exp, [field]: value } : exp
    ));
  };

  // Handle job search
  const handleSearchChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const performJobSearch = async () => {
    try {
      console.log('🔍 Searching jobs with filters:', searchFilters);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (searchFilters.jobTitle) params.append('jobTitle', searchFilters.jobTitle);
      if (searchFilters.location) params.append('location', searchFilters.location);
      if (searchFilters.sector) params.append('sector', searchFilters.sector);
      if (searchFilters.jobType) params.append('jobType', searchFilters.jobType);
      if (searchFilters.experience) params.append('experience', searchFilters.experience);
      if (searchFilters.qualifications) params.append('qualification', searchFilters.qualifications);

      const response = await fetch(`https://tstranscareers.in/api/api/jobs/search/filter?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setRecommendedJobs(data.jobs);
        console.log('✅ Found jobs:', data.jobs.length);
      } else {
        console.error('Job search failed:', data.message);
        setRecommendedJobs([]);
      }
    } catch (error) {
      console.error('Error searching jobs:', error);
      setRecommendedJobs([]);
    }
  };

  // Handle job application
  const applyForJob = async (jobId) => {
    try {
      const job = recommendedJobs.find(j => j.id === jobId);
      if (!job) {
        await showPopup('Job not found', 'Error', { type: 'error' });
        return;
      }

      console.log('📤 Applying for job:', jobId);

      const response = await fetch('https://tstranscareers.in/api/api/applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jobId,
          candidateId: candidateData.id
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Application submitted successfully');
        await showPopup(data.message || 'Application submitted', 'Success', { type: 'success' });

        // Refresh applied jobs list
        await fetchAppliedJobs();
      } else {
        await showPopup(data.message || 'Failed to submit application', 'Error', { type: 'error' });
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      await showPopup('Error submitting application. Please try again.', 'Error', { type: 'error' });
    }
  };
  
  // Fetch applied jobs from backend
  const fetchAppliedJobs = async () => {
    try {
      if (!candidateData?.id) {
        console.warn('⚠️ Cannot fetch applied jobs: candidateData.id is missing');
        return;
      }

      console.log('🔍 Fetching applied jobs for candidate:', candidateData.id);
      const response = await fetch(`https://tstranscareers.in/api/api/applications/candidate/${candidateData.id}`);
      
      console.log('📡 Applied jobs response status:', response.status);
      const data = await response.json();
      console.log('📡 Applied jobs response data:', data);

      if (data.success) {
        setAppliedJobs(data.applications || []);
        console.log('✅ Loaded applied jobs:', data.applications?.length || 0);
      } else {
        console.error('❌ Failed to fetch applied jobs:', data.message);
        setAppliedJobs([]);
      }
    } catch (error) {
      console.error('❌ Error fetching applied jobs:', error);
      setAppliedJobs([]);
    }
  };

  // 🆕 Fetch saved jobs from backend
  const fetchSavedJobs = async () => {
    try {
      if (!candidateData?.id) return;
      
      const response = await fetch(`https://tstranscareers.in/api/api/candidate/${candidateData.id}/saved-jobs`);
      const data = await response.json();

      if (data.success) {
        setSavedJobs(data.savedJobs || []);
        console.log('✅ Loaded saved jobs:', data.savedJobs?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
    }
  };

  // 🆕 Handle save/unsave job
  const handleSaveJob = async (jobId) => {
    try {
      const isSaved = savedJobs.some(sj => sj.id === jobId);

      if (isSaved) {
        // Unsave job
        const response = await fetch(`https://tstranscareers.in/api/api/jobs/${jobId}/save`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ candidateId: candidateData.id })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          await showPopup('Job removed from saved list', 'Success', { type: 'success' });
          fetchSavedJobs(); // Refresh saved jobs
        } else {
          await showPopup(data.message || 'Failed to unsave job', 'Error', { type: 'error' });
        }
      } else {
        // Save job
        const response = await fetch(`https://tstranscareers.in/api/api/jobs/${jobId}/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ candidateId: candidateData.id })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          await showPopup('Job saved successfully!', 'Success', { type: 'success' });
          fetchSavedJobs(); // Refresh saved jobs
        } else {
          await showPopup(data.message || 'Failed to save job', 'Error', { type: 'error' });
        }
      }
      } catch (error) {
      console.error('Error saving/unsaving job:', error);
      await showPopup('Error processing request. Please try again.', 'Error', { type: 'error' });
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      await showPopup('New passwords do not match!', 'Validation', { type: 'info' });
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      await showPopup('New password must be at least 8 characters long!', 'Validation', { type: 'info' });
      return;
    }
    
    // Update password in sessionStorage
    const updatedData = { ...candidateData, password: passwordData.newPassword };
    setItemJSON('candidateData', updatedData);
    setCandidateData(updatedData);
    
    // Clear password modal
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordModal(false);
    
    await showPopup('Password changed successfully!', 'Success', { type: 'success' });
  };

  // Handle logout
  const handleLogout = async () => {
    const ok = await showPopup('Are you sure you want to logout?', 'Confirm', { type: 'info', confirm: true });
    if (!ok) return;
    try {
      clearAllSession();
    } catch (e) { console.warn('Error clearing session', e); }
    setUser(null); // <-- This will also update Header
    navigate('/login', { replace: true });
  };

  // Form validation
  const validateForm = async () => {
    const { candidateName, dateOfBirth, gender, mobileNumber, emailId, district, city, pincode, preferredJobLocation, educationalQualification, preferredJobSector, skills, password } = formData;
    if (!candidateName || !dateOfBirth || !gender || !mobileNumber || !emailId || !district || !city || !pincode || !preferredJobLocation || !educationalQualification || !preferredJobSector || skills.length === 0 || !password) {
      await showPopup('Please fill in all fields', 'Validation', { type: 'info' });
      return false;
    }
    if (mobileNumber.length !== 10) {
      await showPopup('Mobile number must be 10 digits', 'Validation', { type: 'info' });
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(emailId)) {
      await showPopup('Invalid email format', 'Validation', { type: 'info' });
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateForm())) return;

    setIsSubmitting(true);
    const form = new FormData();
    for (let key in formData) {
      if (key === "skills") {
        form.append("skills", formData.skills.join(","));
      } else {
        form.append(key, formData[key]);
      }
    }

    try {
      const response = await fetch("https://tstranscareers.in/api/register", {
        method: "POST",
        body: form
      });

      const result = await response.json();
      if (response.ok) {
        // If backend returns created user, persist session and navigate to dashboard
        if (result.user) {
          // store both session marker and profile used by CandidateDashboard
          setItemJSON('candidateLoggedIn', { id: result.user.id, name: result.user.candidateName });
          setItemJSON('candidateData', result.user);
          // optional: show message and navigate directly to dashboard
          await showPopup(result.message || 'Registration successful. You are now logged in.', 'Success', { type: 'success' });
          navigate('/candidate-dashboard');
        } else {
          // fallback: redirect to login if backend didn't return user
          await showPopup(result.message || "Registration successful. Please login.", 'Success', { type: 'success' });
          navigate("/login");
        }
      } else {
        await showPopup(result.message || "Registration failed", 'Error', { type: 'error' });
      }
    } catch (err) {
      console.error(err);
      await showPopup("Error connecting to server", 'Error', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!candidateData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="candidate-dashboard">

      {/* Sidebar + Content Layout */}
      <div className={"dashboard-layout"}>
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button className="collapse-btn" onClick={() => setSidebarCollapsed(prev => !prev)} aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                <i className="fas fa-bars"></i>
              </button>
              {!sidebarCollapsed && <h3>{t('DASHBOARD')}</h3>}
            </div>
          </div>
          <nav className="sidebar-nav">
            <button title={t('candidate.profile')} className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
              <i className="fas fa-user"></i>
              {!sidebarCollapsed && <span>{t('candidate.profile')}</span>}
            </button>
            <button className={`sidebar-item ${activeTab === 'resume' ? 'active' : ''}`} onClick={() => setActiveTab('resume')}>
              <i className="fas fa-file-alt"></i>
              {!sidebarCollapsed && <span>{t('candidate.resume')}</span>}
            </button>
            <button className={`sidebar-item ${activeTab === 'experience' ? 'active' : ''}`} onClick={() => setActiveTab('experience')}>
              <i className="fas fa-briefcase"></i>
              {!sidebarCollapsed && <span>{t('candidate.workExperience')}</span>}
            </button>
            <button className={`sidebar-item ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>
              <i className="fas fa-search"></i>
              {!sidebarCollapsed && <span>{t('candidate.jobSearch')}</span>}
            </button>
            <button className={`sidebar-item ${activeTab === 'recommended' ? 'active' : ''}`} onClick={() => setActiveTab('recommended')}>
              <i className="fas fa-star"></i>
              {!sidebarCollapsed && <span>{t('candidate.recommendedJobs')}</span>}
            </button>
            <button className={`sidebar-item ${activeTab === 'applied' ? 'active' : ''}`} onClick={() => setActiveTab('applied')}>
              <i className="fas fa-paper-plane"></i>
              {!sidebarCollapsed && <span>{t('candidate.appliedJobs')}</span>}
            </button>
            <button className={`sidebar-item ${activeTab === 'saved' ? 'active' : ''}`} onClick={() => setActiveTab('saved')}>
              <i className="fas fa-bookmark"></i>
              {!sidebarCollapsed && <span>Saved Jobs</span>}
            </button>
          </nav>

          <div className="sidebar-actions">
            {!sidebarCollapsed && (
              <button className="sidebar-action" onClick={() => setShowPasswordModal(true)}>
                <i className="fas fa-key"></i>
                <span>{t('common.changePassword')}</span>
              </button>
            )}

            <div className="logout-wrap">
              <button className={`sidebar-action danger logout-btn ${sidebarCollapsed ? 'icon-only' : ''}`} onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
                {!sidebarCollapsed && <span>{t('common.logout')}</span>}
              </button>
            </div>
          </div>
        </aside>

        <div className="dashboard-main">

          {/* Mobile top nav (visible on small screens) */}
          {isMobile && (
            <div className="mobile-top-nav" role="navigation" aria-label="Mobile Navigation">
            <button className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
              <i className="fas fa-user"></i>
              <span>{t('candidate.profile')}</span>
            </button>
            <button className={`sidebar-item ${activeTab === 'resume' ? 'active' : ''}`} onClick={() => setActiveTab('resume')}>
              <i className="fas fa-file-alt"></i>
              <span>{t('candidate.resume')}</span>
            </button>
            <button className={`sidebar-item ${activeTab === 'experience' ? 'active' : ''}`} onClick={() => setActiveTab('experience')}>
              <i className="fas fa-briefcase"></i>
              <span>{t('candidate.workExperience')}</span>
            </button>
            <button className={`sidebar-item ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>
              <i className="fas fa-search"></i>
              <span>{t('candidate.jobSearch')}</span>
            </button>
            <button className={`sidebar-item ${activeTab === 'recommended' ? 'active' : ''}`} onClick={() => setActiveTab('recommended')}>
              <i className="fas fa-star"></i>
              <span>{t('candidate.recommendedJobs')}</span>
            </button>
            <button className={`sidebar-item ${activeTab === 'applied' ? 'active' : ''}`} onClick={() => setActiveTab('applied')}>
              <i className="fas fa-paper-plane"></i>
              <span>{t('candidate.appliedJobs')}</span>
            </button>
          </div>
          )}

      {/* Main Content */}
      <main className="dashboard-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>{t('candidate.profileDetails')}</h2>
              {!isEditing ? (
                <button className="btn btn-primary" onClick={handleProfileEdit}>
                  <i className="fas fa-edit"></i> {t('common.editProfile')}
                </button>
              ) : (
                <div className="edit-actions">
                  <button className="btn btn-success" onClick={handleProfileSave}>
                    <i className="fas fa-save"></i> {t('common.save')}
                  </button>
                  <button className="btn btn-secondary" onClick={handleProfileCancel}>
                    <i className="fas fa-times"></i> {t('common.cancel')}
                  </button>
                </div>
              )}
            </div>

            <div className="profile-grid">
              <div className="profile-section">
                <h3>{t('candidate.personalInfo')}</h3>
                <div className="form-columns">
                  <div className="form-group">
                    <label>{t('candidate.name')}</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={candidateData.candidateName || ''}
                        onChange={(e) => handleProfileChange('candidateName', e.target.value)}
                      />
                    ) : (
                      <span>{candidateData.candidateName || 'N/A'}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.dob')}</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={formatDate(candidateData.dateOfBirth) !== 'N/A' ? formatDate(candidateData.dateOfBirth) : ''}
                        onChange={(e) => handleProfileChange('dateOfBirth', e.target.value)}
                      />
                    ) : (
                      <span>{formatDate(candidateData.dateOfBirth)}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.gender')}</label>
                    {isEditing ? (
                      <select
                        value={candidateData.gender || ''}
                        onChange={(e) => handleProfileChange('gender', e.target.value)}
                      >
                        <option value="">Select Gender</option>
                        <option value="trans-men">Trans-Men</option>
                        <option value="trans-women">Trans-Women</option>
                        <option value="gender-non-conforming">Gender Non-Conforming</option>
                        <option value="queer">Queer</option>
                      </select>
                    ) : (
                      <span>{candidateData.gender || 'N/A'}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Has Transgender Persons ID</label>
                    <span>{candidateData.hasTGId === 'yes' ? 'Yes' : candidateData.hasTGId === 'no' ? 'No' : 'N/A'}</span>
                  </div>

                  {candidateData.hasTGId === 'yes' && candidateData.tgIdNumber && (
                    <div className="form-group">
                      <label>TG ID Number</label>
                      <span>{candidateData.tgIdNumber}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-section">
                <h3>{t('candidate.contactInfo')}</h3>
                <div className="form-columns">
                  <div className="form-group">
                    <label>{t('candidate.mobile')}</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={candidateData.mobileNumber}
                        onChange={(e) => handleProfileChange('mobileNumber', e.target.value)}
                      />
                    ) : (
                      <span>{candidateData.mobileNumber}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.email')}</label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={candidateData.emailId}
                        onChange={(e) => handleProfileChange('emailId', e.target.value)}
                      />
                    ) : (
                      <span>{candidateData.emailId}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <h3>{t('candidate.addressInfo')}</h3>
                <div className="form-columns">
                  <div className="form-group">
                    <label>{t('candidate.district')}</label>
                    <span>{candidateData.district || 'N/A'}</span>
                  </div>
                  
                  <div className="form-group">
                    <label>Mandal</label>
                    <span>{candidateData.mandal || 'N/A'}</span>
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.city')}</label>
                    <span>{candidateData.city || 'N/A'}</span>
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.pincode')}</label>
                    <span>{candidateData.pincode || 'N/A'}</span>
                  </div>

                  <div className="form-group">
                    <label>Address ID Type</label>
                    <span>{candidateData.addressIdType || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <h3>{t('candidate.jobPreferences')}</h3>
                <div className="form-columns">
                  <div className="form-group">
                    <label>{t('candidate.preferredLocation')}</label>
                    <span>{candidateData.preferredJobLocation || 'N/A'}</span>
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.educationalQualification')}</label>
                    <span>{candidateData.educationalQualification || 'N/A'}</span>
                  </div>
                  
                  <div className="form-group">
                    <label>{t('candidate.preferredSector')}</label>
                    <span>{candidateData.preferredJobSector || 'N/A'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Skills</label>
                  <span>{candidateData.skills || 'N/A'}</span>
                </div>
              </div>

              <div className="profile-section">
                <h3>Documents & Files</h3>
                
                {candidateData.tgIdCard && (
                  <div className="form-group">
                    <label>TG ID Card</label>
                    <div className="file-info">
                      <i className="fas fa-file-image"></i>
                      <button 
                        className="file-view-btn"
                        onClick={() => setPreviewFile({ 
                          url: `https://tstranscareers.in/api/uploads/${candidateData.tgIdCard}`, 
                          label: 'TG ID Card' 
                        })}
                      >
                        View Document
                      </button>
                    </div>
                  </div>
                )}

                {candidateData.addressIdProof && (
                  <div className="form-group">
                    <label>Address ID Proof</label>
                    <div className="file-info">
                      <i className="fas fa-file-image"></i>
                      <button 
                        className="file-view-btn"
                        onClick={() => setPreviewFile({ 
                          url: `https://tstranscareers.in/api/uploads/${candidateData.addressIdProof}`, 
                          label: 'Address ID Proof' 
                        })}
                      >
                        View Document
                      </button>
                    </div>
                  </div>
                )}

                {candidateData.certificateFile && (
                  <div className="form-group">
                    <label>Educational Certificate</label>
                    <div className="file-info">
                      <i className="fas fa-file"></i>
                      <button 
                        className="file-view-btn"
                        onClick={() => setPreviewFile({ 
                          url: `https://tstranscareers.in/api/uploads/${candidateData.certificateFile}`, 
                          label: 'Educational Certificate' 
                        })}
                      >
                        View Certificate
                      </button>
                    </div>
                  </div>
                )}

                {candidateData.resumeFile && (
                  <div className="form-group">
                    <label>Resume File</label>
                    <div className="file-info">
                      <i className="fas fa-file-pdf"></i>
                      <button 
                        className="file-view-btn"
                        onClick={() => setPreviewFile({ 
                          url: `https://tstranscareers.in/api/uploads/${candidateData.resumeFile}`, 
                          label: 'Resume File' 
                        })}
                      >
                        View Resume
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Resume Tab */}
        {activeTab === 'resume' && (
          <div className="tab-content">
            <div className="content-header">
              <h2><i className="fas fa-file-alt"></i> {t('candidate.resumeManagement')}</h2>
            </div>
            
            <div className="resume-section">
              <div className="current-resume">
                <h3>{t('candidate.currentResume')}</h3>
                {candidateData.resumeFile ? (
                  <div className="resume-info">
                    <i className="fas fa-file-pdf" style={{color: '#dc3545', fontSize: '2rem'}}></i>
                    <div className="resume-details">
                      <strong>{candidateData.resumeFile}</strong>
                      <div className="resume-actions">
                        <button 
                          onClick={() => setPreviewFile({ 
                            url: `https://tstranscareers.in/api/uploads/${candidateData.resumeFile}`, 
                            label: 'Resume' 
                          })}
                          className="btn btn-sm btn-primary"
                        >
                          <i className="fas fa-eye"></i> View Resume
                        </button>
                        <a 
                          href={`https://tstranscareers.in/api/uploads/${candidateData.resumeFile}`} 
                          download
                          className="btn btn-sm btn-success"
                        >
                          <i className="fas fa-download"></i> Download
                        </a>
                      </div>
                    </div>
                  </div>
                ) : resume ? (
                  <div className="resume-info">
                    <i className="fas fa-file-pdf"></i>
                    <span>{resume.name}</span>
                    <span className="file-size">({(resume.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                ) : (
                  <div className="no-resume-message">
                    <i className="fas fa-file-excel" style={{fontSize: '3rem', color: '#6c757d', marginBottom: '1rem'}}></i>
                    <p>{t('candidate.noResume') || 'No resume uploaded yet.'}</p>
                  </div>
                )}
              </div>
              
              <div className="upload-resume">
                <h3><i className="fas fa-upload"></i> {t('candidate.uploadNewResume')}</h3>
                <p style={{marginBottom: '1rem', color: '#6c757d', fontSize: '0.9rem'}}>
                  {candidateData.resumeFile ? 'Upload a new resume to replace your current one' : 'Upload your resume file'}
                </p>
                
                <input
                  type="file"
                  id="resume-upload-input"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeUpload}
                  style={{display: 'none'}}
                />
                
                <button 
                  className="btn btn-primary btn-upload-resume"
                  onClick={() => document.getElementById('resume-upload-input').click()}
                >
                  <i className="fas fa-cloud-upload-alt"></i> Choose Resume File
                </button>
                
                <small style={{display: 'block', marginTop: '0.75rem'}}>
                  {t('candidate.resumeFormats') || 'Supported formats: PDF, DOC, DOCX (Max size: 5MB)'}
                </small>
              </div>
            </div>
          </div>
        )}

        {/* Work Experience Tab */}
        {activeTab === 'experience' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>{t('candidate.workExperience')}</h2>
              <button className="btn btn-primary" onClick={addWorkExperience}>
                <i className="fas fa-plus"></i> {t('candidate.addExperience')}
              </button>
            </div>
            
            <div className="experience-list">
              {workExperience.length === 0 ? (
                <p>{t('candidate.noWorkExperience')}</p>
              ) : (
                workExperience.map(exp => (
                  <div key={exp.id} className="experience-item">
                    {exp.isEditing ? (
                      <div className="experience-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('candidate.company')}</label>
                            <input
                              type="text"
                              value={exp.company}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'company', e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('candidate.designation')}</label>
                            <input
                              type="text"
                              value={exp.designation}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'designation', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('candidate.annualSalary')}</label>
                            <input
                              type="text"
                              value={exp.annualSalary}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'annualSalary', e.target.value)}
                              placeholder="e.g., ₹5 LPA"
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('candidate.joiningDate')}</label>
                            <input
                              type="date"
                              value={exp.joiningDate}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'joiningDate', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label>{t('candidate.leavingDate')}</label>
                            <input
                              type="date"
                              value={exp.leavingDate}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'leavingDate', e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('candidate.reasonForLeaving')}</label>
                            <input
                              type="text"
                              value={exp.reason}
                              onChange={(e) => handleWorkExperienceChange(exp.id, 'reason', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="experience-actions">
                          <button className="btn btn-success" onClick={() => saveWorkExperience(exp.id)}>
                            {t('common.save')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="experience-display">
                        <div className="experience-header">
                          <h4>{exp.designation} at {exp.company}</h4>
                          <div className="experience-actions">
                            <button className="btn btn-sm btn-secondary" onClick={() => editWorkExperience(exp.id)}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => deleteWorkExperience(exp.id)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        
                        <div className="experience-details">
                          <p><strong>{t('candidate.annualSalary')}:</strong> {exp.annualSalary}</p>
                          <p><strong>{t('candidate.duration')}:</strong> {exp.joiningDate} {t('common.to')} {exp.leavingDate}</p>
                          <p><strong>{t('candidate.reasonForLeaving')}:</strong> {exp.reason}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Job Search Tab */}
        {activeTab === 'search' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>{t('candidate.jobSearch')}</h2>
            </div>
            
            <div className="search-filters">
              <div className="filter-row">
                <div className="form-group">
                  <label>{t('candidate.jobTitle')}</label>
                  <input
                    type="text"
                    value={searchFilters.jobTitle}
                    onChange={(e) => handleSearchChange('jobTitle', e.target.value)}
                    placeholder="e.g., Software Engineer"
                  />
                </div>
                <div className="form-group">
                  <label>{t('candidate.location')}</label>
                  <input
                    type="text"
                    value={searchFilters.location}
                    onChange={(e) => handleSearchChange('location', e.target.value)}
                    placeholder="e.g., Mumbai"
                  />
                </div>
              </div>
              
              <div className="filter-row">
                <div className="form-group">
                  <label>{t('candidate.sector')}</label>
                  <select
                    value={searchFilters.sector}
                    onChange={(e) => handleSearchChange('sector', e.target.value)}
                  >
                    <option value="">All Sectors</option>
                    <option value="IT/Computer - Software">IT/Computer - Software</option>
                    <option value="IT/Computer - Hardware & Networking">IT/Computer - Hardware & Networking</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Finance">Finance</option>
                    <option value="Healthcare">Healthcare</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('candidate.jobType')}</label>
                  <select
                    value={searchFilters.jobType}
                    onChange={(e) => handleSearchChange('jobType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    {experienceLevels && jobTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="filter-row">
                <div className="form-group">
                  <label>{t('candidate.experience')}</label>
                  <select
                    value={searchFilters.experience}
                    onChange={(e) => handleSearchChange('experience', e.target.value)}
                  >
                    <option value="">All Levels</option>
                    {experienceLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('candidate.ageLimit')}</label>
                  <select
                    value={searchFilters.ageLimit}
                    onChange={(e) => handleSearchChange('ageLimit', e.target.value)}
                  >
                    <option value="">All Ages</option>
                    {ageLimits.map(limit => (
                      <option key={limit} value={limit}>{limit}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="search-actions">
                <button className="btn btn-primary" onClick={performJobSearch}>
                  <i className="fas fa-search"></i> {t('home.searchJobs')}
                </button>
                <button className="btn btn-secondary" onClick={() => setSearchFilters({
                  jobTitle: '', location: '', sector: '', jobType: '', 
                  experience: '', ageLimit: '', annualSalary: '', qualifications: ''
                })}>
                  <i className="fas fa-times"></i> {t('candidate.clearFilters')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recommended Jobs Tab */}
        {activeTab === 'recommended' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>{t('candidate.recommendedJobs')}</h2>
              <p>{t('candidate.recommendedSubtitle')}</p>
            </div>
            
            <div className="jobs-grid">
              {recommendedJobs.length === 0 ? (
                <p>{t('candidate.noJobsFound')}</p>
              ) : (
                recommendedJobs.map(job => (
                  <div key={job.id} className="job-card">
                    <div className="job-header">
                      <h3>{job.job_title || job.title}</h3>
                      <span className="company">{job.company_name || job.company}</span>
                    </div>
                    
                    <div className="job-details">
                      <div className="detail-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{job.place}, {job.district}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-industry"></i>
                        <span>{job.sector}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-clock"></i>
                        <span>{job.job_type || job.jobType}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-briefcase"></i>
                        <span>{job.experience}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-money-bill-wave"></i>
                        <span>₹{job.annual_salary_from || job.salary} - ₹{job.annual_salary_to}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-graduation-cap"></i>
                        <span>{job.qualification || job.qualifications}</span>
                      </div>
                    </div>
                    
                    <div className="job-actions">
                      {appliedJobs.some(app => app.job_id === job.id) ? (
                        <span className="status-text">Application Sent</span>
                      ) : (
                        <button 
                          className="btn btn-primary"
                          onClick={() => applyForJob(job.id)}
                        >
                          {t('candidate.applyNow')}
                        </button>
                      )}
                      <button 
                        className={`btn-save ${savedJobs.some(sj => sj.id === job.id) ? 'saved' : ''}`}
                        onClick={() => handleSaveJob(job.id)}
                        title={savedJobs.some(sj => sj.id === job.id) ? 'Unsave job' : 'Save job for later'}
                      >
                        <i className={`fas fa-bookmark`}></i>
                        {savedJobs.some(sj => sj.id === job.id) ? 'Saved' : 'Save'}
                      </button>
                      <span className="posted-date">{t('candidate.posted')} {new Date(job.created_at || job.postedDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Applied Jobs Tab */}
        {activeTab === 'applied' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>{t('candidate.appliedJobs')}</h2>
              <p>{t('candidate.appliedSubtitle')}</p>
            </div>
            
            <div className="applied-jobs-list">
              {appliedJobs.length === 0 ? (
                <p>{t('candidate.noAppliedJobs')}</p>
              ) : (
                appliedJobs.map(application => (
                  <div key={application.id} className="applied-job-item">
                    <div className="job-info">
                      <h3>{application.job_title || application.title}</h3>
                      <p className="company">{application.company_name || application.company}</p>
                      <p className="location">{application.place}, {application.district}</p>
                    </div>
                    
                    <div className="application-details">
                      <div className="detail-item">
                        <strong>{t('candidate.appliedDate')}:</strong> {new Date(application.applied_date || application.appliedDate).toLocaleDateString()}
                      </div>
                      <div className="detail-item">
                        <strong>{t('candidate.status')}:</strong> 
                        <span className={`status ${(application.application_status || application.status).toLowerCase().replace('_', '-')}`}>
                          {(application.application_status || application.status).replace('_', ' ')}
                        </span>
                      </div>
                      <div className="detail-item">
                        <strong>{t('candidate.salary')}:</strong> ₹{application.annual_salary_from} - ₹{application.annual_salary_to}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Saved Jobs Tab */}
        {activeTab === 'saved' && (
          <div className="tab-content">
            <div className="content-header">
              <h2><i className="fas fa-bookmark"></i> Saved Jobs</h2>
              <p>Jobs you've saved for later review</p>
            </div>
            
            <div className="jobs-grid">
              {savedJobs.length === 0 ? (
                <div className="no-jobs-message">
                  <i className="fas fa-bookmark" style={{fontSize: '3rem', color: '#6c757d', marginBottom: '1rem'}}></i>
                  <p>No saved jobs yet.</p>
                  <p>Save jobs from the Recommended Jobs section to apply later!</p>
                </div>
              ) : (
                savedJobs.map(job => (
                  <div key={job.id} className="job-card">
                    <div className="job-header">
                      <h3>{job.job_title}</h3>
                      <span className="company">{job.company_name}</span>
                    </div>
                    
                    <div className="job-details">
                      <div className="detail-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{job.place}, {job.district}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-industry"></i>
                        <span>{job.sector}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-clock"></i>
                        <span>{job.job_type}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-briefcase"></i>
                        <span>{job.experience}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-money-bill-wave"></i>
                        <span>₹{job.annual_salary_from} - ₹{job.annual_salary_to}</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-graduation-cap"></i>
                        <span>{job.qualification}</span>
                      </div>
                      <div className="detail-item saved-date">
                        <i className="fas fa-calendar"></i>
                        <span>Saved on {new Date(job.saved_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="job-actions">
                      {appliedJobs.some(app => app.job_id === job.id) ? (
                        <span className="status-text">Application Sent</span>
                      ) : (
                        <button 
                          className="btn btn-primary"
                          onClick={() => applyForJob(job.id)}
                        >
                          Apply Now
                        </button>
                      )}
                      <button 
                        className="btn-unsave"
                        onClick={() => handleSaveJob(job.id)}
                        title="Remove from saved jobs"
                      >
                        <i className="fas fa-trash"></i> Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* File Preview Modal */}
      {previewFile && (
        <div className="file-preview">
          <button className="btn back-btn" onClick={() => setPreviewFile(null)}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h3>{previewFile.label}</h3>
          <div className="file-content">
            {previewFile.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
              <img src={previewFile.url} alt={previewFile.label} />
            ) : (
              <iframe src={previewFile.url} title={previewFile.label} />
            )}
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Change Password Help">
          <div className="modal-panel">
            <div className="modal-header">
              <h3>{t('common.changePassword')}</h3>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>If you want to change your password, please contact our helpline.</p>
              <p className="helpline-number">155326</p>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={async () => {
                  try { await navigator.clipboard.writeText('155326'); await showPopup('Copied: 155326', 'Copied', { type: 'success' }); } catch (err) { await showPopup('155326', 'Copied', { type: 'info' }); }
                }}>Copy</button>
                <button className="btn btn-primary" onClick={() => setShowPasswordModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

export default CandidateDashboard;